/**
 * Tabela de campanhas da conta, com ações inline.
 *
 * Métricas são casadas por ID (nunca por nome — duplicar deixa dois com o
 * mesmo nome). A busca é no cliente sobre o que já foi carregado; o filtro de
 * status vai para a Meta (`effective_status`). Paginação por cursor
 * acumulada: "Carregar mais" traz a página seguinte e a lista cresce.
 */

import { useMemo, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { moeda, numero, rotuloResultado, statusDe, OBJETIVO, type Periodo } from "@/lib/meta-ads-format";
import { StatusToggle } from "./StatusToggle";
import { RowActionsMenu } from "./RowActionsMenu";
import { BudgetDialog } from "./BudgetDialog";

type FiltroStatus = "todas" | "ativas" | "pausadas";
const FILTRO_PARA_META: Record<FiltroStatus, Array<"ACTIVE" | "PAUSED"> | undefined> = {
  todas: undefined,
  ativas: ["ACTIVE"],
  pausadas: ["PAUSED"],
};

export function CampaignsTable({
  adAccountId,
  currency,
  periodo,
  canManage,
  canActivate,
  contaBloqueada,
}: {
  adAccountId: string | undefined;
  currency: string | null;
  periodo: Periodo;
  canManage: boolean;
  canActivate: boolean;
  contaBloqueada: boolean;
}) {
  const utils = trpc.useUtils();
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<FiltroStatus>("todas");
  const [editandoVerba, setEditandoVerba] = useState<{ id: string; nome: string; daily: number | null; total: boolean } | null>(null);

  const inputBase = { adAccountId, effectiveStatus: FILTRO_PARA_META[filtro], limit: 50 as const };
  const primeira = trpc.metaAds.campaigns.list.useQuery(inputBase, { staleTime: 30_000, enabled: !!adAccountId });

  // Páginas seguintes acumuladas à mão (o procedure pagina por `after`).
  const [extras, setExtras] = useState<NonNullable<typeof primeira.data>["items"]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const proximoCursor = cursor === undefined ? primeira.data?.nextCursor ?? null : cursor;
  const carregarMais = async () => {
    if (!proximoCursor) return;
    setCarregandoMais(true);
    try {
      const r = await utils.metaAds.campaigns.list.fetch({ ...inputBase, after: proximoCursor });
      setExtras((e) => [...e, ...r.items]);
      setCursor(r.nextCursor);
    } catch (err) {
      toast.error(toUserFriendlyError(err));
    } finally {
      setCarregandoMais(false);
    }
  };
  const mudarFiltro = (f: FiltroStatus) => {
    setFiltro(f);
    setExtras([]);
    setCursor(undefined);
  };

  const metricas = trpc.metaAds.insights.get.useQuery(
    { level: "campaign", datePreset: periodo, adAccountId, limit: 100 },
    { staleTime: 60_000, enabled: !!adAccountId },
  );
  const porCampanha = useMemo(() => {
    const mapa = new Map<string, NonNullable<typeof metricas.data>["items"][number]>();
    for (const l of metricas.data?.items ?? []) if (l.campaignId) mapa.set(String(l.campaignId), l);
    return mapa;
  }, [metricas.data]);

  const todas = useMemo(() => [...(primeira.data?.items ?? []), ...extras], [primeira.data, extras]);
  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return q ? todas.filter((c) => c.name.toLowerCase().includes(q)) : todas;
  }, [todas, busca]);

  const invalidar = () => {
    setExtras([]);
    setCursor(undefined);
    void utils.metaAds.campaigns.list.invalidate();
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="mr-auto font-medium">Campanhas</h2>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome…" className="h-9 w-[220px] pl-9" />
          </div>
          <Select value={filtro} onValueChange={(v) => mudarFiltro(v as FiltroStatus)}>
            <SelectTrigger className="h-9 w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              <SelectItem value="ativas">Ativas</SelectItem>
              <SelectItem value="pausadas">Pausadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {primeira.isLoading && <p className="text-sm text-muted-foreground">Carregando campanhas…</p>}
        {primeira.isError && (
          <p className="text-sm text-red-600">Não consegui carregar as campanhas: {toUserFriendlyError(primeira.error)}</p>
        )}
        {primeira.data && visiveis.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {busca ? "Nenhuma campanha com esse nome." : "Nenhuma campanha nesta conta ainda."}
          </p>
        )}

        {visiveis.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="pb-2 pr-3 font-medium">Campanha</th>
                  <th className="pb-2 pr-3 font-medium">Status</th>
                  <th className="pb-2 pr-3 font-medium">Objetivo</th>
                  <th className="pb-2 pr-3 text-right font-medium">Verba/dia</th>
                  <th className="pb-2 pr-3 text-right font-medium">Gasto</th>
                  <th className="pb-2 pr-3 text-right font-medium">Cliques</th>
                  <th className="pb-2 pr-3 text-right font-medium">Resultados</th>
                  <th className="pb-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((c) => {
                  const m = porCampanha.get(String(c.id));
                  const st = statusDe(c.effectiveStatus);
                  const resultado = m?.resultado ?? null;
                  return (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="max-w-[420px] py-2 pr-3">
                        <Link href={`/ads/campaigns/${c.id}`} className="line-clamp-2 font-medium hover:underline">
                          {c.name}
                        </Link>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          {canActivate && !contaBloqueada && (
                            <StatusToggle
                              objectId={c.id}
                              objectType="campaign"
                              status={c.status}
                              effectiveStatus={c.effectiveStatus}
                              dailyBudgetCents={c.dailyBudgetCents}
                              currency={c.currency ?? currency}
                              onChanged={invalidar}
                            />
                          )}
                          <Badge className={st.className}>{st.label}</Badge>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">{OBJETIVO[c.objective ?? ""] ?? c.objective ?? "—"}</td>
                      <td className="py-2 pr-3 text-right">
                        {c.dailyBudgetCents != null
                          ? moeda(c.dailyBudgetCents, c.currency ?? currency)
                          : c.lifetimeBudgetCents != null
                            ? <span title="Verba total do período">{moeda(c.lifetimeBudgetCents, c.currency ?? currency)} total</span>
                            : <span className="text-muted-foreground" title="Verba definida nos conjuntos">nos conjuntos</span>}
                      </td>
                      <td className="py-2 pr-3 text-right">{moeda(m?.spendCents, currency)}</td>
                      <td className="py-2 pr-3 text-right">{numero(m?.clicks)}</td>
                      <td className="py-2 pr-3 text-right">
                        {numero(resultado?.value)}
                        {resultado && <span className="ml-1 text-xs text-muted-foreground">{rotuloResultado(resultado.type)}</span>}
                      </td>
                      <td className="py-2 text-right">
                        <RowActionsMenu
                          objectType="campaign"
                          objectId={c.id}
                          nome={c.name}
                          adAccountId={c.accountId ?? adAccountId}
                          canManage={canManage}
                          canActivate={canActivate}
                          contaBloqueada={contaBloqueada}
                          onEditBudget={() =>
                            setEditandoVerba({ id: c.id, nome: c.name, daily: c.dailyBudgetCents, total: c.dailyBudgetCents == null && c.lifetimeBudgetCents != null })
                          }
                          onDuplicated={invalidar}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          {proximoCursor ? (
            <Button size="sm" variant="outline" onClick={() => void carregarMais()} disabled={carregandoMais}>
              {carregandoMais && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              Carregar mais campanhas
            </Button>
          ) : (
            <span />
          )}
          {todas.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {proximoCursor ? `Mostrando as primeiras ${todas.length} campanhas` : `${todas.length} campanha(s)`}
            </p>
          )}
        </div>
      </CardContent>

      {editandoVerba && (
        <BudgetDialog
          open
          onOpenChange={(o) => !o && setEditandoVerba(null)}
          objectType="campaign"
          objectId={editandoVerba.id}
          nome={editandoVerba.nome}
          currentDailyCents={editandoVerba.daily}
          usaVerbaTotal={editandoVerba.total}
          currency={currency}
          onSaved={invalidar}
        />
      )}
    </Card>
  );
}
